import React from 'react';
import { NavReconOverviewData } from '../../../../api/rs-hg/nav-recon/types';
import {
  addCommasToNumbersAndRound,
  percentageToNdecialPlacesNoHundred,
} from '../../../../utilities/numberFormatters';
import { sortFundNames } from '../../../../utilities/customSorting';
import { ColumnDef, type SortingState } from '@tanstack/react-table';
import {
  navReconMetricStatusSortFn,
  navReconNumericColumnSort,
  navReconNumericSortFn,
  navReconStatusColumnSort,
} from './navReconTableSorting';
import NavReconOverviewMetricStatusCell from './components/NavReconOverviewMetricStatusCell';
import type { NavReconFundPipelineProgress } from './pipelineMetadata';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import FundNameWithDemoPill from '../../../ui/FundNameWithDemoPill';

export type NavReconOverviewExpandedTableRow = NavReconOverviewData & {
  fund_name: string;
  fund_id?: string;
  fund_name_full: string;
  subfund_code?: string;
  index: number;
  overviewHasPayload: boolean;
  pipelineProgress: NavReconFundPipelineProgress;
  assetClass_Cash?: number;
  assetClass_Equity?: number;
  assetClass_Commodity?: number;
  assetClass_Fixed_Income?: number;
  assetClass_Forex?: number;
  assetClass_Alts?: number;
  alertStatus_six_caceis_returns_status?: string;
  alertStatus_caceis_share_price_returns_status?: string;
  alertStatus_six_share_price_returns_status?: string;
  alertStatus_sc_nav_px_returns_status?: string;
  alertStatus_sc_unit_status?: string;
  isDemoFund: boolean;
};

const STATUS_CELL_WIDTH = '10.5rem';

const renderMetricStatus = (value: string | undefined | null) => (
  <NavReconOverviewMetricStatusCell
    auditStatus={null}
    auditClientComment={null}
    value={value}
  />
);

/** Columns for the NAV-Recon overview (expanded route) table — metrics only, no audit/pipeline. */
export const getNavReconOverviewExpandedColumns = (
  getSorting: () => SortingState,
): ColumnDef<NavReconOverviewExpandedTableRow, any>[] => [
    {
      id: 'expand',
      header: '',
      enableSorting: false,
      meta: { align: 'center', width: '3rem' } as any,
      cell: ({ row }) => {
        const fund = row.original;
        return fund.overviewHasPayload ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            className="rounded p-1 hover:bg-gray-100 focus:outline-none"
          >
            {row.getIsExpanded() ? (
              <HiChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <HiChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </button>
        ) : (
          <div className="w-6 h-6" />
        );
      },
    },
    {
      accessorKey: 'index',
      header: '#',
      enableSorting: false,
      meta: { align: 'center', width: '3rem' } as any,
      cell: ({ row }) => <span className="text-gray-500">{row.index + 1}</span>,
    },
    {
      accessorKey: 'fund_name',
      header: 'Fund ID',
      meta: { align: 'left', minWidth: '9rem' } as any,
      cell: ({ row, getValue }) => (
        <FundNameWithDemoPill
          isDemo={row.original.isDemoFund}
          className="flex-wrap gap-y-1"
        >
          <span className="whitespace-nowrap font-semibold text-gray-900">
            {getValue() as string}
          </span>
        </FundNameWithDemoPill>
      ),
      sortingFn: (rowA, rowB) =>
        sortFundNames(rowA.original.fund_name, rowB.original.fund_name),
    },
    {
      accessorKey: 'subfund_code',
      header: 'NOIND',
      meta: { align: 'left', width: '5rem' } as any,
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap text-gray-900">
          {(getValue() as string | undefined) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'fund_name_full',
      header: 'Fund Name',
      meta: { align: 'left', minWidth: '9rem' } as any,
      cell: ({ getValue }) => (
        <span className="text-gray-900">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'fund_currency',
      header: 'Currency',
      meta: { align: 'center', width: '4%' } as any,
      cell: ({ getValue }) => <span className="text-gray-900">{getValue() as string}</span>,
    },
    {
      accessorKey: 'fund_nav',
      header: 'NAV',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ getValue }) => addCommasToNumbersAndRound(getValue() as number),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'fund_nav', getSorting),
    },
    {
      accessorKey: 'assetClass_Cash',
      header: 'Cash',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ row }) => percentageToNdecialPlacesNoHundred(row.original.asset_class_returns?.Cash, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'assetClass_Cash', getSorting),
    },
    {
      accessorKey: 'assetClass_Equity',
      header: 'Equity',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ row }) => percentageToNdecialPlacesNoHundred(row.original.asset_class_returns?.Equity, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'assetClass_Equity', getSorting),
    },
    {
      accessorKey: 'assetClass_Commodity',
      header: 'Commodity',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ row }) => percentageToNdecialPlacesNoHundred(row.original.asset_class_returns?.Commodity, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'assetClass_Commodity', getSorting),
    },
    {
      accessorKey: 'assetClass_Fixed_Income',
      header: 'Fixed Income',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ row }) => percentageToNdecialPlacesNoHundred(row.original.asset_class_returns?.['Fixed Income'], 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'assetClass_Fixed_Income', getSorting),
    },
    {
      accessorKey: 'assetClass_Forex',
      header: 'Forex',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ row }) => percentageToNdecialPlacesNoHundred(row.original.asset_class_returns?.Forex, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'assetClass_Forex', getSorting),
    },
    {
      accessorKey: 'assetClass_Alts',
      header: 'Alts',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ row }) => percentageToNdecialPlacesNoHundred(row.original.asset_class_returns?.Alts, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'assetClass_Alts', getSorting),
    },
    {
      accessorKey: 'six_weighted_performance',
      header: 'Est Return',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ getValue }) => percentageToNdecialPlacesNoHundred(getValue() as number, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'six_weighted_performance', getSorting),
    },
    {
      accessorKey: 'caceis_weighted_performance',
      header: 'Caceis Return',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ getValue }) => percentageToNdecialPlacesNoHundred(getValue() as number, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'caceis_weighted_performance', getSorting),
    },
    {
      accessorKey: 'overall_sc_px_returns',
      header: 'SC Px Return',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ getValue }) => percentageToNdecialPlacesNoHundred(getValue() as number, 3),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'overall_sc_px_returns', getSorting),
    },
    {
      accessorKey: 'alertStatus_six_caceis_returns_status',
      header: 'Est / Caceis',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row }) =>
        renderMetricStatus(row.original.alert_statuses?.six_caceis_returns_status),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(
          rowA,
          rowB,
          columnId,
          'alertStatus_six_caceis_returns_status',
          getSorting,
        ),
    },
    {
      accessorKey: 'alertStatus_caceis_share_price_returns_status',
      header: 'Caceis / SC Px',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row }) =>
        renderMetricStatus(
          row.original.alert_statuses?.caceis_share_price_returns_status,
        ),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(
          rowA,
          rowB,
          columnId,
          'alertStatus_caceis_share_price_returns_status',
          getSorting,
        ),
    },
    {
      accessorKey: 'alertStatus_six_share_price_returns_status',
      header: 'Est / SC Px',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row }) =>
        renderMetricStatus(row.original.alert_statuses?.six_share_price_returns_status),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(
          rowA,
          rowB,
          columnId,
          'alertStatus_six_share_price_returns_status',
          getSorting,
        ),
    },
    {
      accessorKey: 'alertStatus_sc_nav_px_returns_status',
      header: 'Price / NAV',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row }) =>
        renderMetricStatus(row.original.alert_statuses?.sc_nav_px_returns_status),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(
          rowA,
          rowB,
          columnId,
          'alertStatus_sc_nav_px_returns_status',
          getSorting,
        ),
    },
    {
      accessorKey: 'alertStatus_sc_unit_status',
      header: 'Units Data',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row }) =>
        renderMetricStatus(row.original.alert_statuses?.sc_unit_status),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(
          rowA,
          rowB,
          columnId,
          'alertStatus_sc_unit_status',
          getSorting,
        ),
    },
  ];
